import { Observable, catchError, map, tap, throwError } from 'rxjs';
import {
  AjaxConfig,
  AjaxError,
  AjaxResponse,
  ajax as rxAjax,
} from 'rxjs/ajax';

type AjaxHeaders = Readonly<Record<string, string>>;

export interface AppAjax {
  <T>(config: AjaxConfig): Observable<AjaxResponse<T>>;
  <T>(url: string): Observable<AjaxResponse<T>>;

  get<T>(url: string, headers?: AjaxHeaders): Observable<AjaxResponse<T>>;
  post<T>(url: string, body?: unknown, headers?: AjaxHeaders): Observable<AjaxResponse<T>>;
  put<T>(url: string, body?: unknown, headers?: AjaxHeaders): Observable<AjaxResponse<T>>;
  patch<T>(url: string, body?: unknown, headers?: AjaxHeaders): Observable<AjaxResponse<T>>;
  delete<T>(url: string, headers?: AjaxHeaders): Observable<AjaxResponse<T>>;
  getJSON<T>(url: string, headers?: AjaxHeaders): Observable<T>;
}

const redirectOn401 = <T>(source$: Observable<T>): Observable<T> =>
  source$.pipe(
    catchError((err: unknown) => {
      const ajaxErr = err as AjaxError;

      if (ajaxErr?.status === 401) {
        window.location.assign('/');
      }

      return throwError(() => err);
    })
  );

const createAjax = (<T>(
  urlOrConfig: string | AjaxConfig
): Observable<AjaxResponse<T>> => {
  if (typeof urlOrConfig === 'string') {
    return redirectOn401(rxAjax<T>(urlOrConfig));
  }

  return redirectOn401(rxAjax<T>(urlOrConfig));
}) as AppAjax;

export const ajax: AppAjax = Object.assign(createAjax, {
  get<T>(url: string, headers?: AjaxHeaders): Observable<AjaxResponse<T>> {
    return redirectOn401(
      rxAjax<T>({
        method: 'GET',
        url,
        headers,
      })
    );
  },

  post<T>(
    url: string,
    body?: unknown,
    headers?: AjaxHeaders
  ): Observable<AjaxResponse<T>> {
    return redirectOn401(
      rxAjax<T>({
        method: 'POST',
        url,
        body,
        headers,
      })
    );
  },

  put<T>(
    url: string,
    body?: unknown,
    headers?: AjaxHeaders
  ): Observable<AjaxResponse<T>> {
    return redirectOn401(
      rxAjax<T>({
        method: 'PUT',
        url,
        body,
        headers,
      })
    );
  },

  patch<T>(
    url: string,
    body?: unknown,
    headers?: AjaxHeaders
  ): Observable<AjaxResponse<T>> {
    return redirectOn401(
      rxAjax<T>({
        method: 'PATCH',
        url,
        body,
        headers,
      })
    );
  },

  delete<T>(url: string, headers?: AjaxHeaders): Observable<AjaxResponse<T>> {
    return redirectOn401(
      rxAjax<T>({
        method: 'DELETE',
        url,
        headers,
      })
    );
  },

  getJSON<T>(url: string, headers?: AjaxHeaders): Observable<T> {
    return redirectOn401(
      rxAjax<T>({
        method: 'GET',
        url,
        headers,
      }).pipe(map((response) => response.response as T))
    );
  },
});